import React, { useEffect, useState } from 'react';
import { Col, Form, FormInstance, Grid, NumberInput, Message, Row, Schema } from "rsuite";
import { LatLng } from '@/models/core.model';
import { SpecificStartEndTypeStoryChapterTask, UpsertSpecificStartEndTypeStoryChapterTask } from '@/models/story-chapter-task.model';
import MapMultipleSelector, { MarkerInfo } from '@/components/MapMultipleSelector';

const flatSchema = Schema.Model({
  'startLat': Schema.Types.NumberType().isRequired('Start Latitude is required.'),
  'startLng': Schema.Types.NumberType().isRequired('Start Longitude is required.'),
  'endLat': Schema.Types.NumberType().isRequired('End Latitude is required.'),
  'endLng': Schema.Types.NumberType().isRequired('End Longitude is required.'),
});

const emptyModel = {
  startLat: undefined,
  startLng: undefined,
  endLat: undefined,
  endLng: undefined,
};

interface SpecificStartEndTypeTaskFormFormProps{
  formObject?: SpecificStartEndTypeStoryChapterTask;
  onFormChange: (formValue: any) => void;
  formRef: React.Ref<FormInstance<Record<string, any>>>;
  loading: number;
}

const SpecificStartEndTypeTaskForm: React.FC<SpecificStartEndTypeTaskFormFormProps> = ({ formObject, onFormChange, formRef, loading }) => {
  const [formValue, setFormValue] = useState<{startLat?: number, startLng?: number, endLat?: number, endLng?:number}>(emptyModel);

  useEffect(() => {
    if (formObject) {
      setFormValue({
        startLat: formObject.startPoint.lat,
        startLng: formObject.startPoint.lng,
        endLat: formObject.endPoint.lat,
        endLng: formObject.endPoint.lng,
      });
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  useEffect(() => {
    onFormChange({
      startPoint: {lat: formValue.startLat, lng: formValue.startLng},
      endPoint: {lat: formValue.endLat, lng: formValue.endLng}
    });
  }, [formValue]);


  const getMapValues = (): MarkerInfo[] => {
    const values : MarkerInfo[] = [];
    if(formValue.startLat && formValue.startLng){
      values.push({key:'start', label: 'S', position: {lat: formValue.startLat, lng: formValue.startLng}});
    }
    if(formValue.endLat && formValue.endLng){
      values.push({key:'end', label: 'E', position: {lat: formValue.endLat, lng: formValue.endLng}});
    }
    return values;
  };

  const handleOnMapClick = (position: LatLng) => {
    if(!formValue.startLat || !formValue.startLng){
      setFormValue(pre => ({...pre, startLat: position.lat, startLng: position.lng}));
    } else if(!formValue.endLat || !formValue.endLng){
      setFormValue(pre => ({...pre, endLat: position.lat, endLng: position.lng}));
    }
  };

  const handleMarkerDrag = (key: string, position: LatLng) => {
    if(key == 'start'){
      setFormValue(pre => ({...pre, startLat: position.lat, startLng: position.lng}));
    } else if(key == 'end'){
      setFormValue(pre => ({...pre, endLat: position.lat, endLng: position.lng}));
    }
  };

  return (
    <Form name='upsert-specific-start-end-task-type' fluid
        ref={formRef}
        formValue={formValue}
        model={flatSchema}
        disabled={!!loading}
    >    
      <Grid w='100%' fluid>
        <Row>
          <Col span={{xs:24}} style={{textAlign: 'center'}}>
            <Form.Group>
              <Form.Label>{
                (!formValue.startLat || !formValue.startLng) ? 
                'Click on map to select START point' : 
                (!formValue.endLat || !formValue.endLng) ? 
                'Click on map to select END point' : 
                'Drag START or END markers'
              }</Form.Label>
              <MapMultipleSelector
                value={getMapValues()}
                onClick={handleOnMapClick}
                onMarkerDrag={handleMarkerDrag}
                height='400px'
                defaultZoom={12}
              />                  
            </Form.Group>
          </Col>
        </Row>        
        <Row>
          <Col span={{xs:24, xl:12}}>
            <Form.Group controlId="startLat">
              <Form.Label>Start Latitude</Form.Label>
              <Form.Control name="startLat" accepter={NumberInput}
                value={formValue.startLat}
                onChange={val => setFormValue(pre => ({...pre, startLat: Number(val)}))}
              />

            </Form.Group>                  
         </Col>
         <Col span={{xs:24, xl:12}}>
            <Form.Group controlId="startLng">
              <Form.Label>Start Longitude</Form.Label>
              <Form.Control name="startLng" accepter={NumberInput}
                value={formValue.startLng}
                onChange={val => setFormValue(pre => ({...pre, startLng: Number(val)}))}
              />
            </Form.Group>
         </Col>
        </Row>
        <Row>
          <Col span={{xs:24, xl:12}}>
            <Form.Group controlId="endLng">
              <Form.Label>End Latitude</Form.Label>
              <Form.Control name="endLat" accepter={NumberInput}
                value={formValue.endLat}
                onChange={val => setFormValue(pre => ({...pre, endLat:Number(val)}))}
              />  
            </Form.Group>                  
         </Col>
         <Col span={{xs:24, xl:12}}>
            <Form.Group controlId="endLng">
              <Form.Label>End Longitude</Form.Label>
              <Form.Control name="endLng" accepter={NumberInput}
                value={formValue.endLng}
                onChange={val => setFormValue(pre => ({...pre, endLng:Number(val)}))}
              />
            </Form.Group>
         </Col>
        </Row>
        <Row>
          <Message showIcon type="info">
            User should start running exactly in start point and finish the running in end point. Amount: Duration in minute.
          </Message>
        </Row>         
      </Grid>
    </Form>
  );
};

export default SpecificStartEndTypeTaskForm;